import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdLocationOn, MdOutlinePeople, MdWorkOutline, MdPerson, MdUploadFile, MdAssignment } from "react-icons/md";
import { ArrowLeft } from "lucide-react";
import Header from "../../Header";
import LeftNavBar from "../../LeftNavBar";
import PageHeader from "../../PageHeader";
import { PageHeaderName } from "../../../StyledCss/styledComponents";
import { SecondaryButton } from "../../../StyledCss/styledbtn";
import { getHiringJobById } from "../../../api/services/hroneApi";
import { isPdfResumeUrl, normalizeJob } from "../../../util/hiringUtils";
import "../JobDetailsPage/index.css";
import "../CandidateDetailsPage/index.css";
import "./index.css";

const STATUS_LABELS = {
  APPLIED: "Applied",
  SHORTLISTED: "Shortlisted",
  HIRED: "Hired",
  REJECTED: "Rejected",
};

const ReferralDetailsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const orgDetails = JSON.parse(localStorage.getItem("org"));
  const primaryColor = orgDetails?.primaryColor || "#e86020";

  const referral = location.state?.referral;
  const scope = location.state?.scope === "all" ? "all" : "mine";
  const backLabel = scope === "all" ? "All Referrals" : "My Referrals";
  const handleBack = () => navigate(-1);

  const [job, setJob] = useState(null);
  const [jobLoading, setJobLoading] = useState(!!referral?.jobId);

  useEffect(() => {
    if (!referral?.jobId) return;
    let cancelled = false;

    const load = async () => {
      setJobLoading(true);
      try {
        const res = await getHiringJobById(referral.jobId);
        const data = await res.json();
        if (!cancelled && data.status) {
          setJob(normalizeJob(data.data));
        }
      } catch {
        // job overview is a nice-to-have here; referral details below still render
      } finally {
        if (!cancelled) setJobLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [referral?.jobId]);

  if (!referral) {
    return (
      <>
        <Header />
        <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
          <LeftNavBar />
          <div className="page-content-wrapper d-flex justify-content-center align-items-center">
            <div style={{ textAlign: "center", color: "#646569" }}>
              <p style={{ fontSize: "18px", fontWeight: 500, marginBottom: "12px" }}>Referral not found</p>
              <SecondaryButton onClick={() => navigate(-1)}>Back</SecondaryButton>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="d-flex" style={{ "--hiring-primary": primaryColor }}>
        <LeftNavBar />
        <div className="page-content-wrapper hiring-detail-fade-in">
          <PageHeader>
            <PageHeaderName>
              <ArrowLeft size={20} onClick={handleBack} style={{ cursor: "pointer", marginRight: "8px" }} />
              {backLabel}
            </PageHeaderName>
          </PageHeader>

          {job && (
            <div className="job-details-summary-card job-details-summary-header">
              <div className="job-details-title-row">
                <div>
                  <p className="job-details-title">{job.title}</p>
                  <p className="job-details-dept">{job.designation}</p>
                </div>
                <span className="job-type-badge">{job.employmentType}</span>
              </div>

              <div className="job-details-meta-row">
                <span className="job-details-meta-pill">
                  <MdLocationOn className="job-details-meta-icon" />{job.location}
                </span>
                <span className="job-details-meta-pill">
                  <MdOutlinePeople className="job-details-meta-icon" />
                  {job.vacancies} {job.vacancies === 1 ? "Vacancy" : "Vacancies"}
                </span>
                <span className="job-details-meta-pill">
                  <MdWorkOutline className="job-details-meta-icon" />{job.experience}
                </span>
              </div>
            </div>
          )}

          {jobLoading && !job && (
            <div className="job-details-summary-card">
              <p style={{ color: "#9e9e9e", fontSize: "14px" }}>Loading job overview…</p>
            </div>
          )}

          {job && (
            <div className="job-details-content-box">
              <div className="job-details-overview">
                <div className="job-details-overview-section">
                  <p className="job-details-overview-section-title">Job Description</p>
                  <p className="job-details-description">{job.description}</p>
                </div>
                <div className="job-details-overview-section">
                  <p className="job-details-overview-section-title">Required Skills</p>
                  <div className="job-details-skills-list">
                    {job.skills.map((skill) => (
                      <span
                        key={skill}
                        className="job-details-skill-tag"
                        style={{
                          backgroundColor: primaryColor + "0d",
                          borderColor: primaryColor + "40",
                          color: primaryColor,
                        }}
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="job-details-content-box">
            <p className="job-details-overview-section-title" style={{ padding: "16px 24px 0" }}>Candidate Overview</p>
            <div className="cd-overview" style={{ padding: "16px 24px 24px" }}>
              <div className="cd-info-card">
                <p className="cd-info-card-title">
                  <MdPerson size={13} style={{ marginRight: 5 }} />Personal Information
                </p>
                <div className="cd-info-card-grid">
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Name</span>
                    <span className="cd-info-field-value">{referral.name || `${referral.firstName} ${referral.lastName}`}</span>
                  </div>
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Email</span>
                    <span className="cd-info-field-value">{referral.email || "—"}</span>
                  </div>
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Mobile</span>
                    <span className="cd-info-field-value">{referral.mobile || "—"}</span>
                  </div>
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Location</span>
                    <span className="cd-info-field-value">{referral.currentLocation || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="cd-info-card">
                <p className="cd-info-card-title">
                  <MdAssignment size={13} style={{ marginRight: 5 }} />Application Information
                </p>
                <div className="cd-info-card-grid">
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Status</span>
                    <span className="cd-info-field-value">{STATUS_LABELS[referral.status] || referral.status}</span>
                  </div>
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Applied Date</span>
                    <span className="cd-info-field-value">{referral.appliedDate || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="cd-info-card">
                <p className="cd-info-card-title">
                  <MdWorkOutline size={13} style={{ marginRight: 5 }} />Professional Information
                </p>
                <div className="cd-info-card-grid">
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Experience</span>
                    <span className="cd-info-field-value">{referral.totalExperience || "—"}</span>
                  </div>
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Current Company</span>
                    <span className="cd-info-field-value">{referral.currentCompany || "—"}</span>
                  </div>
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Current CTC</span>
                    <span className="cd-info-field-value">{referral.currentCTC || "—"}</span>
                  </div>
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Expected CTC</span>
                    <span className="cd-info-field-value">{referral.expectedCTC || "—"}</span>
                  </div>
                  <div className="cd-info-field">
                    <span className="cd-info-field-label">Notice Period</span>
                    <span className="cd-info-field-value">{referral.noticePeriod || "—"}</span>
                  </div>
                </div>
              </div>
            </div>

            <p className="job-details-overview-section-title" style={{ padding: "0 24px 8px" }}>Resume</p>
            {isPdfResumeUrl(referral.resumeUrl, referral.resumeFileName) ? (
              <div className="cd-resume-viewer" style={{ margin: "0 24px 24px" }}>
                <iframe
                  src={`${referral.resumeUrl}#navpanes=0&toolbar=0`}
                  title="Candidate Resume"
                  className="cd-resume-iframe"
                />
              </div>
            ) : (
              <div className="cd-resume-empty">
                <div className="cd-resume-empty-icon">
                  <MdUploadFile size={52} />
                </div>
                {referral.resumeUrl ? (
                  <>
                    <p className="cd-resume-empty-title">Preview unavailable</p>
                    <p className="cd-resume-empty-sub">
                      Only PDF files can be viewed here. Ask HR to replace the resume with a PDF.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="cd-resume-empty-title">No Resume Available</p>
                    <p className="cd-resume-empty-sub">No resume was submitted with this application.</p>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ReferralDetailsPage;
